import { cloneVehicleInto, type VehicleInput, type VehicleState } from '../game/vehicle';
import { reconcileGroundVehicle, type PendingFrame } from './reconcile';
import type { ServerToClientMsg } from './protocol';

/**
 * prd2 Week 3 Day 16 — WebSocket client with input buffer + snapshot reconciliation.
 */
export class MultiplayerClient {
  private ws: WebSocket | null = null;
  private readonly pending: PendingFrame[] = [];
  private lastSnapshot: Extract<ServerToClientMsg, { type: 'snapshot' }> | null =
    null;
  private lastAppliedTick = -1;
  /** Latest remote player pose for rendering (Day 17 will interpolate). */
  remoteDisplay: VehicleState | null = null;

  playerId: 0 | 1 = 0;
  connected = false;
  /** Two players present — game sim runs. */
  running = false;

  constructor(private readonly url: string) {}

  connect(
    onWelcome: (vehicles: [VehicleState, VehicleState]) => void,
    onError?: (e: Event) => void,
  ): void {
    this.ws = new WebSocket(this.url);
    this.ws.addEventListener('open', () => {
      this.sendJson({ type: 'hello' });
    });
    this.ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(String(ev.data)) as ServerToClientMsg;
        if (msg.type === 'welcome') {
          this.playerId = msg.playerId;
          this.connected = true;
          onWelcome(msg.vehicles);
        } else if (msg.type === 'snapshot') {
          this.lastSnapshot = msg;
          const rid = this.playerId === 0 ? 1 : 0;
          const remote = msg.vehicles[rid];
          if (!this.remoteDisplay) {
            this.remoteDisplay = JSON.parse(JSON.stringify(remote)) as VehicleState;
          } else {
            cloneVehicleInto(this.remoteDisplay, remote);
          }
        } else if (msg.type === 'wait') {
          this.running = msg.players >= 2;
        }
      } catch {
        /* ignore */
      }
    });
    this.ws.addEventListener('error', onError ?? (() => {}));
    this.ws.addEventListener('close', () => {
      this.connected = false;
      this.running = false;
    });
  }

  dispose(): void {
    this.ws?.close();
    this.ws = null;
    this.pending.length = 0;
    this.lastSnapshot = null;
    this.lastAppliedTick = -1;
    this.remoteDisplay = null;
  }

  /** Call at the start of each ground frame before applying new input. */
  reconcileLocalVehicle(vehicle: VehicleState): void {
    const snap = this.lastSnapshot;
    if (!snap || snap.tick <= this.lastAppliedTick) return;
    this.lastAppliedTick = snap.tick;
    const ack = snap.acks[this.playerId];
    reconcileGroundVehicle(
      vehicle,
      snap.vehicles[this.playerId],
      ack,
      this.pending,
    );
  }

  /**
   * Record this frame’s input, run prediction, and send to server.
   * Order: must run after `reconcileLocalVehicle`.
   */
  predictAndSend(
    seq: number,
    dt: number,
    input: VehicleInput,
    stepPredicted: () => void,
  ): void {
    this.pending.push({ seq, dt, input });
    stepPredicted();
    this.sendJson({ type: 'input', seq, dt, input });
  }

  private sendJson(payload: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }
}
